/**
 * ProfileButton.js
 *
 * A responsive profile button component for the Navbar.
 *
 * 🎨 Design Philosophy:
 * - Minimal, tactile, and Apple-inspired.
 * - Hover-based Sign Out button for desktop users.
 * - Dedicated Sign In / Sign Out button for mobile users.
 *
 * 🧠 Behavior:
 * - If no user → opens SignIn modal.
 * - If user logged in → shows hoverable "Sign Out" option.
 * - Mobile view → replaces hover with an always-visible button.
 */

import React, { useState } from "react";
import { User } from "lucide-react";
import { useClientAuth } from "../../hooks/clientAuth";
import toast from "react-hot-toast";

const ProfileButton = ({ onOpenSignInModal }) => {
    const { user, signOutUser } = useClientAuth();
    const [hovered, setHovered] = useState(false);

    /**
     * Handles signing out of the user account.
     */
    const handleSignOut = async () => {
        try {
            await signOutUser();
            setHovered(false);
            toast.success("User signed out successfully.");
        } catch (err) {
            console.error("Error signing out:", err);
        }
    };

    /**
     * Determines what happens when the profile icon is clicked.
     * - If logged in → triggers sign-out (desktop hover alternative).
     * - If not logged in → opens the sign-in modal.
     */
    const handleProfileClick = () => {
        if (user) {
            handleSignOut();
        } else {
            onOpenSignInModal();
        }
    };

    return (
        <div className="relative flex items-center">
            {/* ---------- Profile Icon Button (All Screens) ---------- */}
            <button
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={handleProfileClick}
                className={`
                    rounded-full p-2 transition-all duration-300
                    border border-white/40
                    ${user ? "bg-white/20 text-white" : "bg-transparent text-white"}
                    hover:bg-white/30 hover:scale-105
                    backdrop-blur-md shadow-[inset_0_1px_0px_rgba(255,255,255,0.6),0_2px_8px_rgba(0,0,0,0.3)]
                    focus:outline-none
                `}
                aria-label="Profile"
            >
                <User size={18} />
            </button>

            {/* ---------- Hover Sign-Out Tooltip (Desktop Only) ---------- */}
            {user && hovered && (
                <div
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    onClick={handleSignOut}
                    className="
                        absolute top-10 right-0
                        bg-white/90 text-zinc-800
                        text-xs font-medium
                        border border-zinc-300 rounded-lg
                        shadow-lg cursor-pointer select-none
                        px-3 py-1 transition-all duration-200
                        hover:bg-zinc-100 hover:scale-105
                        backdrop-blur-md
                    "
                >
                    Sign Out
                </div>
            )}

            {/* ---------- Mobile Sign In/Out Button ---------- */}
            <button
                onClick={handleProfileClick}
                className="
                    md:hidden ml-3 px-3 py-1
                    text-xs rounded-full
                    bg-white/15 border border-white/30 text-white
                    hover:bg-white/25 hover:scale-105
                    transition-all duration-300
                "
            >
                {user ? "Sign Out" : "Sign In"}
            </button>
        </div>
    );
};

export default ProfileButton;